import { Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IntakeAnswers, IntakeAction } from '../types';
import { AppointmentDateTimePicker } from '../../../../components/AppointmentDateTimePicker';
import { useIntakeDataContext } from '../IntakeDataContext';
import type { BookableSlot } from '../intakeFhirMapping';
import { bookableDateKeysInWindow } from '../intakeDataService';
import { getIntakeBookingWindow } from '../bookingConstraints';
import { formatUsTimeZoneLabel } from '../../../../lib/usTimeZoneLabel';
import { DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE } from '../../../../features/calendar/providerAvailabilitySchedule';

interface StepBookingProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
}

export function StepBooking({ answers, dispatch }: StepBookingProps): JSX.Element {
  const isIntake = (answers.sessionType ?? 'intake') === 'intake';
  const sessionType = answers.sessionType ?? 'intake';
  const { isMock, fetchSlotsForDate } = useIntakeDataContext();
  const { minDate, maxDate } = useMemo(() => getIntakeBookingWindow(), []);

  const [slots, setSlots] = useState<BookableSlot[]>([]);
  const [loadedDateKey, setLoadedDateKey] = useState<string | null>(null);
  const [availableWeekdays, setAvailableWeekdays] = useState<string[]>([]);
  const [timeZone, setTimeZone] = useState(DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const loadSeqRef = useRef(0);
  const inFlightByDateRef = useRef<Map<string, Promise<void>>>(new Map());
  const pendingDateRef = useRef<string | null>(null);

  const availableDates = useMemo(() => {
    if (isMock || availableWeekdays.length === 0) return undefined;
    return bookableDateKeysInWindow(minDate, maxDate, availableWeekdays);
  }, [isMock, availableWeekdays, minDate, maxDate]);

  useEffect(() => {
    setSlots([]);
    setLoadedDateKey(null);
    setAvailableWeekdays([]);
    setAvailabilityError(null);
    setSlotsLoading(false);
    inFlightByDateRef.current.clear();
    pendingDateRef.current = null;
  }, [answers.selectedProviderId, sessionType]);

  const loadSlotsForDate = useCallback(
    (dateKey: string): Promise<void> => {
      if (isMock || !answers.selectedProviderId) {
        return Promise.resolve();
      }

      if (import.meta.env.DEV) {
        console.debug('[StepBooking] loadSlotsForDate', {
          dateKey,
          providerId: answers.selectedProviderId,
          sessionType,
        });
      }

      const existing = inFlightByDateRef.current.get(dateKey);
      if (existing) return existing;

      const loadSeq = ++loadSeqRef.current;
      setSlotsLoading(true);
      setAvailabilityError(null);
      setSlots([]);

      const promise = (async () => {
        try {
          const result = await fetchSlotsForDate(
            answers.selectedProviderId!,
            sessionType,
            dateKey,
          );
          if (loadSeq !== loadSeqRef.current) return;

          setSlots(result.slots);
          setLoadedDateKey(dateKey);
          setAvailableWeekdays(result.availableWeekdays);
          setTimeZone(result.timeZone);
        } catch {
          if (loadSeq !== loadSeqRef.current) return;
          setSlots([]);
          setLoadedDateKey(null);
          setAvailabilityError('Could not load availability for this date.');
        } finally {
          if (loadSeq === loadSeqRef.current) {
            setSlotsLoading(false);
          }
          inFlightByDateRef.current.delete(dateKey);
        }
      })();

      inFlightByDateRef.current.set(dateKey, promise);
      return promise;
    },
    [isMock, answers.selectedProviderId, sessionType, fetchSlotsForDate],
  );

  const liveTimeSlotsForDate = useCallback(
    (dateKey: string): string[] => {
      if (dateKey !== loadedDateKey) return [];
      return slots.map((s) => {
        const hhmm = s.timeKey;
        return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
      });
    },
    [slots, loadedDateKey],
  );

  const handleDateChange = useCallback(
    (dateKey: string) => {
      if (dateKey === loadedDateKey) return;
      if (dateKey === pendingDateRef.current) return;

      pendingDateRef.current = dateKey;
      queueMicrotask(() => {
        pendingDateRef.current = null;
      });

      dispatch({ type: 'SET_BOOKING_DATE', payload: dateKey });
      dispatch({ type: 'SET_BOOKING_TIME', payload: null });
      void loadSlotsForDate(dateKey);
    },
    [dispatch, loadSlotsForDate, loadedDateKey],
  );

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Choose a day and time for your {isIntake ? 'intake session' : 'consultation'} with your provider.
      </Text>
      {availabilityError ? (
        <Text size="sm" c="red">
          {availabilityError}
        </Text>
      ) : null}
      {!slotsLoading && loadedDateKey && slots.length === 0 ? (
        <Text size="sm" c="dimmed">
          No open times on this day. Try another date.
        </Text>
      ) : null}
      <AppointmentDateTimePicker
        selectedDate={answers.bookingDate}
        selectedTime={answers.bookingTime}
        onDateChange={handleDateChange}
        onTimeChange={(time) => dispatch({ type: 'SET_BOOKING_TIME', payload: time })}
        minDate={minDate}
        maxDate={maxDate}
        availabilityKey={answers.selectedProviderId ?? undefined}
        liveTimeSlots={!isMock ? liveTimeSlotsForDate : undefined}
        availableDates={!isMock ? availableDates : undefined}
        timeZoneLabel={formatUsTimeZoneLabel(timeZone)}
        timesLoading={slotsLoading}
      />
    </Stack>
  );
}
